import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { supabase } from '../../lib/supabase'
import { Button, Card, Input, MultiImageUploader, Badge, DarkModeToggleCompact } from '../../components/ui'
import { HelpTip } from '../../components/ui/HelpTip'
import { ArrowLeft, Play, Plus, Trash2, ImageIcon, Type, List, Monitor, Calendar, ArrowUp, ArrowDown, Mic, Sparkles, Volume2, VolumeX, Maximize, Minimize, Star } from 'lucide-react'
import { soundFx } from '../../lib/soundEffects'
import { SidebarRotator } from '../../components/layout/SidebarRotator'
import { ImageSlideshow } from '../../components/layout/ImageSlideshow'
import { CinematicSponsorWall } from '../../components/layout/CinematicSponsorWall'
import { useMatches } from '../../hooks/useMatches'
import { usePoules } from '../../hooks/usePoules'
import { useTeams } from '../../hooks/useTeams'
import { OpeningSpeechGenerator } from '../../components/tools/OpeningSpeechGenerator'
import { WinnerPredictor } from '../../components/tools/WinnerPredictor'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'

const AutoFitText = ({ content, minSize = 12, maxSize = 60, className = '' }) => {
    const containerRef = useRef(null)
    const textRef = useRef(null)

    useEffect(() => {
        const container = containerRef.current
        const text = textRef.current
        if (!container || !text) return

        let currentSize = maxSize
        text.style.fontSize = `${currentSize}px`

        const fit = () => {
            if (!container || !text) return
            while (text.scrollHeight > container.clientHeight && currentSize > minSize) {
                currentSize -= 1
                text.style.fontSize = `${currentSize}px`
            }
        }
        
        const timer = setTimeout(fit, 50)
        return () => clearTimeout(timer)
    }, [content, maxSize, minSize])

    return (
        <div ref={containerRef} className="w-full h-full flex flex-col items-center overflow-hidden relative">
            <p ref={textRef} className={`my-auto text-center leading-relaxed font-medium whitespace-pre-wrap break-words text-balance ${className}`}>
                {content}
            </p>
        </div>
    )
}

export const PresentationPage = () => {
    const { tournamentId } = useParams()
    const navigate = useNavigate()
    const [slides, setSlides] = useState([])
    const [loading, setLoading] = useState(true)
    const { t } = useTranslation()

    const [isPlaying, setIsPlaying] = useState(false)
    const [activeTab, setActiveTab] = useState('tv') // 'tv' or 'speech'
    const [tournamentName, setTournamentName] = useState('')
    const [logoPreview, setLogoPreview] = useState(null)
    const [progress, setProgress] = useState(0)
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    // Sub-slide for Standings (rotating poules)
    const [currentPouleIndex, setCurrentPouleIndex] = useState(0)
    const [isUnmuted, setIsUnmuted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [theme, setTheme] = useState({
        color: '#4f46e5',
        background: '',
        logoLeft: '',
        logoRight: '',
        sport_type: 'goals'
    });

    const [voiceAnnouncer, setVoiceAnnouncer] = useState(() => localStorage.getItem('propoule_voice_announcer') === 'true')
    const [showPrintFlyer, setShowPrintFlyer] = useState(false)

    const { matches } = useMatches(tournamentId)
    const { poules } = usePoules(tournamentId)
    const { teams } = useTeams(tournamentId)
    const [pouleStandings, setPouleStandings] = useState({})

    useEffect(() => {
        calculateStandings()
    }, [matches, teams, poules])

    // Detect browser fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch((err) => {
                console.error("Error enabling fullscreen:", err);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        fetchSlides()
        fetchTheme()
    }, [tournamentId])

    const fetchTheme = async () => {
        const { data } = await supabase
            .from('tournaments')
            .select('name, theme_color, background_image, sponsor_left_image, sponsor_right_image, sport_type')
            .eq('id', tournamentId)
            .single();

        if (data) {
            setTournamentName(data.name);
            setTheme({
                color: data.theme_color || '#4f46e5',
                background: data.background_image || '',
                logoLeft: data.sponsor_left_image,
                logoRight: data.sponsor_right_image,
                sport_type: data.sport_type || 'goals'
            });
        }
    };

    const calculateStandings = () => {
        const statsByPoule = {}
        // Init stats
        teams.forEach(team => {
            const pId = team.poule_id || 'unknown'
            if (!statsByPoule[pId]) statsByPoule[pId] = []
            statsByPoule[pId].push({
                ...team,
                played: 0, won: 0, drawn: 0, lost: 0,
                goalsFor: 0, goalsAgainst: 0, goalDifference: 0, points: 0
            })
        })

        // Process matches
        matches.forEach(match => {
            if (match.type !== 'group') return
            if (match.score_home === null || match.score_away === null) return
            Object.keys(statsByPoule).forEach(pId => {
                const homeParams = statsByPoule[pId].find(t => t.id === match.home_team_id)
                const awayParams = statsByPoule[pId].find(t => t.id === match.away_team_id)

                if (homeParams) {
                    homeParams.played++
                    homeParams.goalsFor += match.score_home
                    homeParams.goalsAgainst += match.score_away
                    if (match.score_home > match.score_away) { homeParams.won++; homeParams.points += 3 }
                    else if (match.score_home < match.score_away) { homeParams.lost++ }
                    else { homeParams.drawn++; homeParams.points += 1 }
                }
                if (awayParams) {
                    awayParams.played++
                    awayParams.goalsFor += match.score_away
                    awayParams.goalsAgainst += match.score_home
                    if (match.score_away > match.score_home) { awayParams.won++; awayParams.points += 3 }
                    else if (match.score_away < match.score_home) { awayParams.lost++ }
                    else { awayParams.drawn++; awayParams.points += 1 }
                }
            })
        })

        // Sort
        Object.keys(statsByPoule).forEach(pId => {
            statsByPoule[pId].forEach(t => { t.goalDifference = t.goalsFor - t.goalsAgainst })
            statsByPoule[pId].sort((a, b) => {
                if (b.points !== a.points) return b.points - a.points
                if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
                return b.goalsFor - a.goalsFor
            })
        })

        setPouleStandings(statsByPoule)
    }

    const fetchSlides = async () => {
        const { data, error } = await supabase
            .from('slides')
            .select('*')
            .eq('tournament_id', tournamentId)
            .order('sort_order', { ascending: true })

        if (data) setSlides(data)
        setLoading(false)
    }

    const moveSlide = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === slides.length - 1) return;

        const newSlides = [...slides];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap elements
        [newSlides[index], newSlides[swapIndex]] = [newSlides[swapIndex], newSlides[index]];

        // Update sort_order locally
        newSlides.forEach((s, i) => s.sort_order = i);

        setSlides(newSlides);

        // Update DB
        const slideA = newSlides[index];
        const slideB = newSlides[swapIndex];

        try {
            await Promise.all([
                supabase.from('slides').update({ sort_order: slideA.sort_order }).eq('id', slideA.id),
                supabase.from('slides').update({ sort_order: slideB.sort_order }).eq('id', slideB.id)
            ]);
        } catch (error) {
            console.error("Error reordering:", error);
        }
    };

    const addSlide = async (type) => {
        const newSlide = {
            tournament_id: tournamentId,
            type,
            title: type === 'custom' ? '' : type === 'standings' ? t('presentation_page.slide_type_standings') : type === 'matches' ? t('presentation_page.slide_type_matches') : type === 'program' ? t('presentation_page.slide_type_program') : type === 'image' ? t('presentation_page.slide_type_image') : type === 'sponsors' ? 'Premium Sponsor Wall' : t('presentation_page.slide_type_custom'),
            content: type === 'custom' ? t('presentation_page.default_custom_content') : type === 'image' ? '' : null,
            duration: 15,
            sort_order: slides.length
        }

        const { data, error } = await supabase.from('slides').insert(newSlide).select().single()

        if (error) {
            console.error('Error adding slide:', error)
            alert(t('presentation_page.error_add_slide') + error.message)
            return
        }

        if (data) setSlides([...slides, data])
    }

    const deleteSlide = async (id) => {
        await supabase.from('slides').delete().eq('id', id)
        setSlides(slides.filter(s => s.id !== id))
    }

    // --- ESC KEY TO CLOSE PRESENTATION ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isPlaying) {
                setIsPlaying(false)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isPlaying])

    // --- VIEWER MODE ---
    useEffect(() => {
        let timer
        let progressTimer
        if (isPlaying && slides.length > 0) {
            const currentSlide = slides[currentSlideIndex]
            const duration = (currentSlide.duration || 10) * 1000

            // Progress bar
            const step = 100
            let current = 0
            progressTimer = setInterval(() => {
                current += step
                setProgress((current / duration) * 100)
            }, step)

            // Switch slide
            timer = setTimeout(() => {
                const nextIndex = (currentSlideIndex + 1) % slides.length
                const nextSlide = slides[nextIndex]

                // Cinematic Match Moments (Audio)
                if (nextSlide.type === 'matches') {
                    soundFx.play('chord')
                } else if (nextSlide.type === 'program') {
                    soundFx.play('pop')
                } else if (nextSlide.type === 'custom') {
                    soundFx.play('focus')
                } else {
                    soundFx.play('navigate')
                }

                setCurrentSlideIndex(nextIndex)
                setProgress(0)
                setCurrentPouleIndex(0) // Reset poule rotation on slide change
            }, duration)
        }
        return () => {
            clearTimeout(timer)
            clearInterval(progressTimer)
        }
    }, [isPlaying, currentSlideIndex, slides])

    // --- SUB-SLIDE ROTATION (STANDINGS) ---
    useEffect(() => {
        let pouleTimer
        if (isPlaying && slides[currentSlideIndex]?.type === 'standings' && poules.length > 1) {
            pouleTimer = setInterval(() => {
                setCurrentPouleIndex(prev => (prev + 1) % poules.length)
            }, 5000) // Rotate poule every 5 seconds
        } else {
            setCurrentPouleIndex(0)
        }
        return () => clearInterval(pouleTimer)
    }, [isPlaying, currentSlideIndex, poules.length])

    useEffect(() => {
        localStorage.setItem('propoule_voice_announcer', voiceAnnouncer ? 'true' : 'false')
    }, [voiceAnnouncer])

    useEffect(() => {
        if (showPrintFlyer) {
            const timer = setTimeout(() => {
                window.print()
                setShowPrintFlyer(false)
            }, 800)
            return () => clearTimeout(timer)
        }
    }, [showPrintFlyer])

    useEffect(() => {
        if (isPlaying && voiceAnnouncer && slides.length > 0) {
            const currentSlide = slides[currentSlideIndex]
            if (!currentSlide) return

            const speakTimer = setTimeout(() => {
                let speechText = ''
                
                if (currentSlide.type === 'custom') {
                    speechText = `Beste toeschouwers, hier is een belangrijke mededeling. ${currentSlide.title}. ${currentSlide.content || ''}`
                } else if (currentSlide.type === 'standings') {
                    const poule = poules[currentPouleIndex] || poules[0]
                    const standings = pouleStandings[poule?.id] || []
                    if (poule && standings.length > 0) {
                        const leader = standings[0].name
                        const points = standings[0].points
                        speechText = `Tijd voor een tussenstand in ${poule.name}. ${leader} staat momenteel aan kop met ${points} punten. `
                        if (standings.length > 1) {
                            speechText += `Gevolgd door ${standings[1].name} op de tweede plaats.`
                        }
                    } else {
                        speechText = `Hier is de live tussenstand van het toernooi.`
                    }
                } else if (currentSlide.type === 'matches') {
                    const recentMatches = matches.filter(m => m.score_home !== null)
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    if (recentMatches.length > 0) {
                        const m = recentMatches[0]
                        speechText = `De uitslag van de laatst gespeelde wedstrijd is binnen. ${m.home_team?.name || 'Home'} tegen ${m.away_team?.name || 'Away'}, eindigde in ${m.score_home} tegen ${m.score_away}.`
                    } else {
                        speechText = `Hier zijn de laatste wedstrijd uitslagen op de schermen.`
                    }
                } else if (currentSlide.type === 'program') {
                    const upcomingMatches = matches.filter(m => m.score_home === null)
                        .sort((a, b) => (a.match_time || '23:59').localeCompare(b.match_time || '23:59'))
                    if (upcomingMatches.length > 0) {
                        const m = upcomingMatches[0]
                        const time = m.match_time ? m.match_time.slice(0, 5) : ''
                        const fieldText = m.field ? ` op veld ${m.field}` : ''
                        speechText = `Bereid je voor op de volgende ronde. Om ${time} speelt ${m.home_team?.name || 'Home'} tegen ${m.away_team?.name || 'Away'}${fieldText}. Veel succes aan beide teams.`
                    } else {
                        speechText = `Bekijk het komende programma op de schermen voor de volgende wedstrijden.`
                    }
                } else if (currentSlide.type === 'image' || currentSlide.type === 'sponsors') {
                    speechText = `Mede mogelijk gemaakt door onze geweldige sponsors. Wij danken hen voor hun steun aan de club.`
                }

                if (speechText && window.speechSynthesis) {
                    window.speechSynthesis.cancel()
                    const utterance = new SpeechSynthesisUtterance(speechText)
                    utterance.lang = 'nl-NL'
                    utterance.rate = 0.95
                    utterance.volume = 1.0
                    
                    const voices = window.speechSynthesis.getVoices()
                    const nlVoice = voices.find(v => v.lang.startsWith('nl'))
                    if (nlVoice) utterance.voice = nlVoice
                    
                    window.speechSynthesis.speak(utterance)
                }
            }, 600)

            return () => clearTimeout(speakTimer)
        }
    }, [isPlaying, currentSlideIndex, voiceAnnouncer, slides, poules, pouleStandings, matches])

    // --- NEWS TICKER ITEMS ---
    const getNewsTickerItems = () => {
        const items = [{ type: 'text', content: `${t('presentation_page.tv_welcome')} ${tournamentName || '...'}! 🏆` }];

        matches
            .filter(m => m.score_home !== null)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
            .forEach(m => {
                items.push({
                    type: 'match',
                    home: m.home_team?.name || 'Home',
                    away: m.away_team?.name || 'Away',
                    score_home: m.score_home,
                    score_away: m.score_away,
                    logo_home: m.home_team?.logo_url,
                    logo_away: m.away_team?.logo_url
                });
            });

        poules.forEach(p => {
            if (pouleStandings[p.id]?.[0]) {
                items.push({ type: 'leader', poule: p.name, team: pouleStandings[p.id][0].name });
            }
        });

        const tips = [t('presentation_page.tv_tips_1'), t('presentation_page.tv_tips_2')]
        tips.forEach(tip => items.push({ type: 'text', content: tip }));
        return items;
    }

    const isEsports = theme.sport_type === 'esports'

    if (isPlaying) {
        const slide = slides[currentSlideIndex]

        if (!slide) {
            return createPortal(
                <div className="fixed inset-0 bg-gray-950 text-white flex items-center justify-center z-[9999] font-sans">
                    <div className="text-center p-8 glass-strong rounded-3xl border border-white/10 max-w-md w-full mx-4">
                        <p className="text-2xl font-bold mb-6">{t('presentation_page.tv_no_slides')}</p>
                        <Button variant="primary" onClick={() => setIsPlaying(false)} className="w-full">{t('presentation_page.tv_close')}</Button>
                    </div>
                </div>,
                document.body
            )
        }

        return createPortal(
            <div className={`fixed inset-0 z-[9999] flex flex-col font-sans overflow-hidden select-none transition-colors duration-500 ${
                isEsports ? 'bg-[#05030a] text-white' : 'bg-[#0f1115] text-white'
            }`}>
                {/* Modern Gradient Background */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className={`absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] animate-pulse-slow ${
                        isEsports ? 'bg-purple-600/15' : 'bg-blue-600/20'
                    }`}></div>
                    <div className={`absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] animate-pulse-slow ${
                        isEsports ? 'bg-cyan-500/15' : 'bg-purple-600/20'
                    }`} style={{ animationDelay: '2s' }}></div>
                    {isEsports && (
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.02)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                    )}
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.dev/noise.svg')] opacity-15"></div>
                </div>

                {/* Progress Bar */}
                <div className="h-1.5 bg-white/5 w-full z-50 relative">
                    <div
                        className={`h-full shadow-[0_0_10px_rgba(168,85,247,0.5)] transition-all quantity-linear duration-100 ${
                            isEsports 
                                ? 'bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-500' 
                                : 'bg-gradient-to-r from-blue-400 to-purple-500'
                        }`}
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Main Cinematic Content Area */}
                <div className="flex-1 flex relative z-10 p-4 md:p-8 gap-6 overflow-hidden">

                    {/* Left Sidebar (Rotator) */}
                    <div className="hidden xl:flex flex-col w-80 shrink-0">
                        <div className={`h-full backdrop-blur-xl rounded-3xl p-6 border shadow-2xl flex flex-col justify-center transition-all ${
                            isEsports 
                                ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_40px_rgba(168,85,247,0.15)]' 
                                : 'bg-white/5 border-white/10'
                        }`}>
                            {theme.logoLeft && (() => {
                                const parseImages = (jsonString) => {
                                    try {
                                        if (jsonString.startsWith('[')) return JSON.parse(jsonString);
                                        return [jsonString];
                                    } catch (e) { return [jsonString]; }
                                };
                                const images = parseImages(theme.logoLeft);
                                if (images.length > 0) return <SidebarRotator images={images} type="club" />;
                                return null;
                            })()}
                        </div>
                    </div>

                    {/* Central Stage */}
                    <div className="flex-1 flex flex-col items-center justify-center relative w-full overflow-hidden">

                        {/* 1. CUSTOM TEXT SLIDE */}
                        {slide.type === 'custom' && (
                            <div className="w-full h-full flex items-center justify-center p-8 md:p-12 relative overflow-hidden animate-fade-in-up">
                                <div className={`absolute inset-0 bg-gradient-to-br opacity-50 ${
                                    isEsports ? 'from-purple-900/40 to-black' : 'from-blue-900/20 to-transparent'
                                }`}></div>
                                <div className={`relative z-10 w-full max-w-6xl backdrop-blur-2xl p-8 md:p-12 rounded-[2.5rem] text-center border shadow-2xl overflow-hidden flex flex-col items-center justify-center transition-all duration-300 max-h-[85vh] h-full ${
                                    isEsports 
                                        ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white/5 border-white/10'
                                }`}>
                                    <div className={`absolute top-0 left-0 w-full h-1.5 ${
                                        isEsports ? 'bg-gradient-to-r from-purple-500 to-cyan-400' : 'bg-gradient-to-r from-orange-400 to-red-500'
                                    }`}></div>
                                    {slide.title && slide.title.trim() !== '' && (
                                        <>
                                            <h1 className="text-[clamp(2rem,5vw,4.5rem)] font-black mb-6 text-white tracking-tight leading-tight drop-shadow-xl shrink-0 text-balance">
                                                {slide.title}
                                            </h1>
                                            <div className="w-16 h-1 bg-white/10 rounded-full mx-auto mb-6 shrink-0"></div>
                                        </>
                                    )}
                                    <div className="w-full flex-1 overflow-hidden min-h-[100px] flex items-center justify-center">
                                        <AutoFitText 
                                            content={slide.content}
                                            maxSize={40}
                                            minSize={12}
                                            className="text-gray-200"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* 1.5. PREMIUM SPONSOR WALL */}
                        {slide.type === 'sponsors' && (
                            <CinematicSponsorWall tournamentId={tournamentId} isEsports={isEsports} />
                        )}

                        {/* 2. IMAGE / SPONSOR SLIDE */}
                        {slide.type === 'image' && (() => {
                            const parseContent = (c) => {
                                let parsed = [];
                                try {
                                    if (c && c.startsWith('[')) parsed = JSON.parse(c);
                                    else parsed = c ? [c] : [];
                                } catch (e) { parsed = c ? [c] : []; }
                                
                                return parsed.filter(url => 
                                    url && 
                                    typeof url === 'string' &&
                                    url.trim() !== '' &&
                                    url !== 'null' &&
                                    url !== 'undefined' &&
                                    !url.includes('example.com') &&
                                    !url.includes('voorbeeld.nl')
                                );
                            };
                            const images = parseContent(slide.image_url || slide.content);
                            if (images.length === 0) return null;
                            return (
                                <div className="w-full h-full flex items-center justify-center p-4">
                                    <ImageSlideshow 
                                        images={images} 
                                        title={slide.title === t('presentation_page.slide_type_image') ? '' : slide.title} 
                                    />
                                </div>
                            )
                        })()}

                        {/* 3. STANDINGS SLIDE (SINGLE POULE FOCUS) */}
                        {slide.type === 'standings' && (
                            <div className="w-full h-full flex flex-col animate-fade-in max-w-5xl mx-auto">
                                <header className="mb-6 flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-xl border ${
                                            isEsports ? 'bg-purple-500/10 border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : 'bg-blue-500/20 border-blue-500/30'
                                        }`}>
                                            <List size={32} className={isEsports ? 'text-purple-400' : 'text-blue-400'} />
                                        </div>
                                        <div>
                                            <h2 className="text-5xl font-black text-white tracking-tight">{t('presentation_page.tv_standings')}</h2>
                                            <p className={`text-xl font-medium ${isEsports ? 'text-purple-300/80' : 'text-blue-200/80'}`}>{t('presentation_page.tv_live_update')}</p>
                                        </div>
                                    </div>
                                    {poules.length > 1 && (
                                        <div className="flex gap-2">
                                            {poules.map((_, idx) => (
                                                <div key={idx} className={`h-2 rounded-full transition-all duration-300 ${
                                                    idx === currentPouleIndex 
                                                        ? isEsports ? 'w-8 bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]' : 'w-8 bg-blue-500' 
                                                        : 'w-2 bg-white/20'
                                                }`} />
                                            ))}
                                        </div>
                                    )}
                                </header>

                                <div className="flex-1 overflow-hidden relative">
                                    {poules.length > 0 && (() => {
                                        const poule = poules[currentPouleIndex] || poules[0] // Fallback
                                        const standings = pouleStandings[poule.id] || []

                                        return (
                                            <div key={poule.id} className={`backdrop-blur-xl rounded-[2.5rem] p-8 border shadow-2xl flex flex-col h-full animate-fade-in transition-all ${
                                                isEsports 
                                                    ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' 
                                                    : 'bg-[#1c1f26]/80 border-white/10'
                                            }`}>
                                                <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4 shrink-0">
                                                    <h3 className="text-4xl font-black text-white truncate">{poule.name}</h3>
                                                    <Badge className={`px-4 py-1.5 text-sm font-bold tracking-widest uppercase ${
                                                        isEsports ? 'bg-purple-600/30 text-purple-300 border border-purple-500/30 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'bg-blue-600 text-white'
                                                    }`}>{t('presentation_page.tv_poule')}</Badge>
                                                </div>

                                                <div className="flex-1 overflow-hidden flex flex-col">
                                                    {/* Table Header */}
                                                    <div className="grid grid-cols-[4rem_1fr_6rem_6rem] gap-6 px-6 py-4 text-sm md:text-base font-bold text-gray-400 uppercase tracking-widest bg-black/30 rounded-xl mb-4 shrink-0">
                                                        <div className="text-center text-lg">#</div>
                                                        <div className="text-lg">{t('presentation_page.tv_team')}</div>
                                                        <div className="text-center text-lg">{t('presentation_page.tv_played')}</div>
                                                        <div className="text-center text-white text-lg">{t('presentation_page.tv_points')}</div>
                                                    </div>

                                                    {/* Rows */}
                                                    <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar">
                                                        {standings.map((team, idx) => (
                                                            <div
                                                                key={team.id}
                                                                className={`grid grid-cols-[4rem_1fr_6rem_6rem] gap-6 items-center px-6 py-4 rounded-2xl border transition-all ${
                                                                    idx < 2
                                                                        ? isEsports
                                                                            ? 'bg-purple-500/15 border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)]'
                                                                            : 'bg-blue-500/10 border-blue-500/20 shadow-lg'
                                                                        : 'bg-white/5 border-transparent hover:bg-white/10'
                                                                }`}
                                                            >
                                                                <div className="flex justify-center">
                                                                    <span className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-xl text-lg md:text-xl font-black leading-none ${
                                                                        idx === 0 
                                                                            ? isEsports 
                                                                                ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30 scale-110' 
                                                                                : 'bg-yellow-400 text-black shadow-lg scale-110' 
                                                                            : idx === 1 
                                                                                ? isEsports 
                                                                                    ? 'bg-cyan-500 text-black shadow-md scale-105' 
                                                                                    : 'bg-gray-300 text-black shadow-md' 
                                                                                : 'text-gray-500 bg-white/5'
                                                                    }`}>
                                                                        {idx + 1}
                                                                    </span>
                                                                </div>
                                                                <div className="font-bold text-xl md:text-2xl text-gray-100 truncate min-w-0 flex items-center gap-4" title={team.name}>
                                                                    {team.logo_url && <img src={team.logo_url} alt="" className="w-8 h-8 md:w-10 md:h-10 object-contain drop-shadow-md" onError={(e) => e.target.style.display = 'none'} />}
                                                                    <span className="truncate">{team.name || "Team ?"}</span>
                                                                </div>
                                                                <div className="text-center font-mono text-gray-400 text-xl md:text-2xl">{team.played}</div>
                                                                <div className="text-center font-mono font-black text-white text-2xl md:text-3xl tracking-tight">{team.points}</div>
                                                            </div>
                                                        ))}
                                                        {standings.length === 0 && <p className="text-gray-500 text-center italic py-12 text-xl">{t('presentation_page.tv_no_teams')}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* 4. MATCHES SLIDE (FULL WIDTH & VISIBLE NAMES) */}
                        {slide.type === 'matches' && (
                            <div className="w-full h-full flex flex-col animate-fade-in px-4">
                                <header className="mb-8 flex items-center justify-center text-center shrink-0">
                                    <div>
                                        <div className={`inline-flex items-center justify-center p-4 rounded-full border mb-4 backdrop-blur-sm ${
                                            isEsports ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]' : 'bg-green-500/10 border-green-500/20'
                                        }`}>
                                            <Monitor size={48} className={isEsports ? 'text-purple-400' : 'text-green-400'} />
                                        </div>
                                        <h2 className="text-6xl font-black text-white tracking-tight drop-shadow-lg">{t('presentation_page.tv_latest_results')}</h2>
                                    </div>
                                </header>

                                <div className="grid grid-cols-1 gap-4 max-w-7xl w-full mx-auto content-start">
                                    {matches
                                        .filter(m => m.score_home !== null)
                                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                        .slice(0, 5)
                                        .map((match, i) => (
                                            <div
                                                key={match.id}
                                                className={`group relative border p-4 rounded-2xl shadow-xl grid grid-cols-[1fr_auto_1fr] gap-6 items-center hover:bg-[#232730]/40 transition-colors ${
                                                    isEsports 
                                                        ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.1)]' 
                                                        : 'bg-[#1c1f26] border-white/5'
                                                }`}
                                            >
                                                <div className={`absolute left-0 top-0 bottom-0 w-2 rounded-l-2xl ${
                                                    isEsports ? 'bg-purple-500 shadow-[0_0_10px_#a855f7]' : 'bg-yellow-500'
                                                }`}></div>

                                                {/* Home Team */}
                                                <div className="flex items-center justify-end gap-4">
                                                    <span className={`text-2xl md:text-3xl font-black block leading-tight text-right ${match.score_home > match.score_away ? 'text-green-400 animate-pulse' : 'text-gray-100'}`}>
                                                        {match.home_team?.name || 'Home'}
                                                    </span>
                                                    {match.home_team?.logo_url ? (
                                                        <img src={match.home_team.logo_url} className="w-12 h-12 md:w-16 md:h-16 object-contain shrink-0 drop-shadow-lg" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-12 h-12 bg-white/5 rounded-full" />}
                                                </div>

                                                {/* Score Badge */}
                                                <div className={`px-6 py-3 rounded-xl border min-w-[140px] text-center shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.6)] ${
                                                    isEsports ? 'bg-[#150a2e]/60 border-purple-500/30' : 'bg-black/60 border-white/10'
                                                }`}>
                                                    <span className={`font-mono text-5xl md:text-6xl font-black tracking-widest drop-shadow-2xl ${
                                                        isEsports ? 'text-cyan-400' : 'text-yellow-400'
                                                    }`}>
                                                        {match.score_home}-{match.score_away}
                                                    </span>
                                                </div>

                                                {/* Away Team */}
                                                <div className="flex items-center justify-start gap-4">
                                                    {match.away_team?.logo_url ? (
                                                        <img src={match.away_team.logo_url} className="w-12 h-12 md:w-16 md:h-16 object-contain shrink-0 drop-shadow-lg" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-12 h-12 bg-white/5 rounded-full" />}
                                                    <span className={`text-2xl md:text-3xl font-black block leading-tight text-left ${match.score_away > match.score_home ? 'text-green-400 animate-pulse' : 'text-gray-100'}`}>
                                                        {match.away_team?.name || 'Away'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    {matches.filter(m => m.score_home !== null).length === 0 && (
                                        <div className="text-center py-12">
                                            <p className="text-2xl text-gray-500 font-bold">{t('presentation_page.tv_no_results')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 5. PROGRAM SLIDE (FULL WIDTH & VISIBLE NAMES) */}
                        {slide.type === 'program' && (
                            <div className="w-full h-full flex flex-col animate-fade-in px-4">
                                <header className="mb-8 flex items-center justify-center text-center shrink-0">
                                    <div>
                                        <div className={`inline-flex items-center justify-center p-4 rounded-full border mb-4 backdrop-blur-sm ${
                                            isEsports ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]' : 'bg-purple-500/10 border-purple-500/20'
                                        }`}>
                                            <Calendar size={48} className={isEsports ? 'text-cyan-400' : 'text-purple-400'} />
                                        </div>
                                        <h2 className="text-6xl font-black text-white tracking-tight drop-shadow-lg">{t('presentation_page.tv_program')}</h2>
                                    </div>
                                </header>

                                <div className="grid grid-cols-1 gap-4 max-w-7xl w-full mx-auto content-start">
                                    {matches
                                        .filter(m => m.score_home === null)
                                        .sort((a, b) => (a.match_time || '23:59').localeCompare(b.match_time || '23:59'))
                                        .slice(0, 5)
                                        .map((match, i) => (
                                            <div
                                                key={match.id}
                                                className={`border p-4 rounded-2xl shadow-xl grid grid-cols-[1fr_auto_1fr] gap-6 items-center hover:bg-[#232730]/40 transition-colors ${
                                                    isEsports 
                                                        ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.1)]' 
                                                        : 'bg-[#1c1f26] border-white/5'
                                                }`}
                                            >
                                                {/* Home Team */}
                                                <div className="flex items-center justify-end gap-4">
                                                    <span className="text-2xl md:text-3xl font-bold text-gray-300 block leading-tight text-right">
                                                        {match.home_team?.name || 'Home'}
                                                    </span>
                                                    {match.home_team?.logo_url ? (
                                                        <img src={match.home_team.logo_url} className="w-12 h-12 md:w-16 md:h-16 object-contain shrink-0 drop-shadow-md" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-12 h-12 bg-white/5 rounded-full" />}
                                                </div>

                                                {/* Time Badge */}
                                                <div className="flex flex-col items-center justify-center min-w-[140px] shrink-0">
                                                    <div className={`px-5 py-2 border font-black text-3xl md:text-4xl rounded-xl mb-1 shadow-inner ${
                                                        isEsports 
                                                            ? 'bg-purple-500/20 text-cyan-300 border-cyan-500/25 shadow-[0_0_10px_rgba(6,182,212,0.15)]' 
                                                            : 'bg-purple-600/20 text-purple-300 border-purple-500/30'
                                                    }`}>
                                                        {match.match_time ? match.match_time.slice(0, 5) : '--:--'}
                                                    </div>
                                                    <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">{match.field ? `${t('presentation_page.tv_field')} ${match.field}` : `${t('presentation_page.tv_field')}?`}</span>
                                                </div>

                                                {/* Away Team */}
                                                <div className="flex items-center justify-start gap-4">
                                                    {match.away_team?.logo_url ? (
                                                        <img src={match.away_team.logo_url} className="w-12 h-12 md:w-16 md:h-16 object-contain shrink-0 drop-shadow-md" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-12 h-12 bg-white/5 rounded-full" />}
                                                    <span className="text-2xl md:text-3xl font-bold text-gray-300 block leading-tight text-left">
                                                        {match.away_team?.name || 'Away'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    {matches.filter(m => m.score_home === null).length === 0 && (
                                        <div className="text-center py-12">
                                            <p className="text-2xl text-gray-500 font-bold">{t('presentation_page.tv_no_matches')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Sidebar (Rotator) */}
                    <div className="hidden xl:flex flex-col w-80 shrink-0">
                        <div className={`h-full backdrop-blur-xl rounded-3xl p-6 border shadow-2xl flex flex-col justify-center transition-all ${
                            isEsports 
                                ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_40px_rgba(168,85,247,0.15)]' 
                                : 'bg-white/5 border-white/10'
                        }`}>
                            {theme.logoRight && (() => {
                                const parseImages = (jsonString) => {
                                    try {
                                        if (jsonString.startsWith('[')) return JSON.parse(jsonString);
                                        return [jsonString];
                                    } catch (e) { return [jsonString]; }
                                };
                                const images = parseImages(theme.logoRight);
                                if (images.length > 0) return <SidebarRotator images={images} type="sponsor" />;
                                return null;
                            })()}
                        </div>
                    </div>

                    {/* Glowing QR Code Floating Broadcast Widget */}
                    <div className={`absolute top-6 right-16 p-3 rounded-2xl border backdrop-blur-xl shadow-2xl z-50 flex items-center gap-3 transition-all hover:scale-105 duration-300 ${
                        isEsports 
                            ? 'bg-[#0b0717]/90 border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.25)]' 
                            : 'bg-black/70 border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
                    }`}>
                        <div className="flex flex-col text-right">
                            <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold leading-tight">{t('public.share_title') || 'Volg Live'}</span>
                            <span className={`text-xs font-black tracking-tight ${isEsports ? 'text-cyan-400 font-bold' : 'text-white'}`}>{t('presentation_page.tv_live_update') || 'Scan QR'}</span>
                        </div>
                        <div className={`p-1 bg-white rounded-lg ${isEsports ? 'shadow-[0_0_12px_rgba(6,182,212,0.4)]' : ''}`}>
                            <QRCodeSVG 
                                value={window.location.origin + '/live/' + tournamentId} 
                                size={52} 
                                level="H" 
                                includeMargin={false}
                                fgColor={isEsports ? '#05030a' : '#000000'}
                            />
                        </div>
                    </div>

                    {/* Subtle Controls Overlay */}
                    <div className="absolute top-6 left-6 z-50 flex items-center gap-3 bg-black/40 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/5 shadow-lg">
                        <Button 
                            onClick={() => setIsPlaying(false)}
                            className="bg-transparent hover:bg-white/10 text-white font-bold py-1 px-3 text-xs border border-white/10 rounded-xl"
                        >
                            {t('presentation_page.tv_close')} (ESC)
                        </Button>
                        <div className="w-[1px] h-4 bg-white/10"></div>
                        <button 
                            onClick={() => setIsUnmuted(prev => {
                                const next = !prev;
                                soundFx.toggle(next);
                                return next;
                            })}
                            className="text-gray-400 hover:text-white transition-colors p-1"
                            title={isUnmuted ? "Dempen / Mute" : "Geluid inschakelen / Unmute"}
                        >
                            {isUnmuted ? <Volume2 size={18} /> : <VolumeX size={18} className="text-red-400 animate-pulse" />}
                        </button>
                        <div className="w-[1px] h-4 bg-white/10"></div>
                        <button 
                            onClick={() => setVoiceAnnouncer(prev => !prev)}
                            className={`transition-colors p-1 flex items-center gap-1.5 text-xs font-bold ${voiceAnnouncer ? 'text-purple-400' : 'text-gray-400 hover:text-white'}`}
                            title={voiceAnnouncer ? "Live Omroeper Actief" : "Live Omroeper Uitgeschakeld"}
                        >
                            <Mic size={18} className={voiceAnnouncer ? 'animate-pulse' : ''} />
                            <span className="hidden sm:inline">{voiceAnnouncer ? 'OMROEP AAN' : 'OMROEP UIT'}</span>
                        </button>
                        <div className="w-[1px] h-4 bg-white/10"></div>
                        <button 
                            onClick={toggleFullscreen}
                            className="text-gray-400 hover:text-white transition-colors p-1"
                            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                        </button>
                    </div>

                    {/* LIVE NEWS TICKER FOOTER */}
                    <div className={`absolute bottom-0 left-0 w-full h-16 border-t flex items-center z-50 overflow-hidden shadow-2xl transition-colors ${
                        isEsports ? 'bg-[#06040c] border-purple-500/20' : 'bg-[#111] border-white/10'
                    }`}>
                        <style>{`@keyframes scroll-left { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }`}</style>
                        <div className={`px-6 h-full flex items-center justify-center shrink-0 z-20 shadow-lg relative overflow-hidden ${
                            isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-red-600'
                        }`}>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" style={{ animationDuration: '2s' }}></div>
                            <span className="font-black text-white uppercase tracking-widest text-sm md:text-base animate-pulse shadow-sm">{t('presentation_page.tv_live')}</span>
                        </div>
                        <div className="flex-1 overflow-hidden relative h-full flex items-center">
                            <div className="whitespace-nowrap flex items-center gap-16 text-lg md:text-xl font-bold text-white pl-[100%]" style={{ animation: 'scroll-left 45s linear infinite' }}>
                                {getNewsTickerItems().map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        {/* Separator */}
                                        {idx > 0 && <span className={`w-2 h-2 rounded-full mx-2 animate-pulse ${
                                            isEsports ? 'bg-purple-500 shadow-[0_0_10px_#a855f7]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]'
                                        }`}></span>}

                                        {/* CONTENT TYPES */}
                                        {item.type === 'text' && <span>{item.content}</span>}

                                        {item.type === 'match' && (
                                            <div className="flex items-center gap-3 bg-white/5 px-4 py-1.5 rounded-full border border-white/10 backdrop-blur-md">
                                                <span className="text-gray-400 text-xs md:text-sm uppercase tracking-wider font-medium">{t('presentation_page.tv_result')}:</span>
                                                <div className="flex items-center gap-2">
                                                    {item.logo_home && <img src={item.logo_home} className="w-5 h-5 md:w-6 md:h-6 object-contain" onError={(e) => e.target.style.display = 'none'} />}
                                                    <span className={item.score_home > item.score_away ? 'text-green-400' : 'text-gray-200'}>{item.home}</span>
                                                </div>
                                                <span className={`font-black px-2 rounded shadow-sm text-base md:text-lg ${
                                                    isEsports ? 'bg-purple-500 text-white' : 'bg-yellow-500 text-black'
                                                }`}>
                                                    {item.score_home}-{item.score_away}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className={item.score_away > item.score_home ? 'text-green-400' : 'text-gray-200'}>{item.away}</span>
                                                    {item.logo_away && <img src={item.logo_away} className="w-5 h-5 md:w-6 md:h-6 object-contain" onError={(e) => e.target.style.display = 'none'} />}
                                                </div>
                                            </div>
                                        )}

                                        {item.type === 'leader' && (
                                            <div className="flex items-center gap-2">
                                                <span>👑</span>
                                                <span className="text-gray-400">{t('presentation_page.tv_leader')} {item.poule}:</span>
                                                <span className={isEsports ? 'text-purple-300 shadow-purple-500/10' : 'text-yellow-400'}>{item.team}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>,
            document.body
        )
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container animate-fadeIn">
                {/* Hero Section */}
                <div className="mb-8 animate-slideUp">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate('/admin/dashboard')} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('presentation_page.btn_back')}
                    </Button>

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(79,70,229,0.3)]'
                            }`}>
                                <Monitor className="text-white animate-pulse" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-3xl md:text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('presentation_page.title')}
                                </h1>
                                <p className={`text-sm md:text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                                    {t('presentation_page.subtitle')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                            <DarkModeToggleCompact />
                            {activeTab === 'tv' && (
                                <div className="flex gap-3">
                                    <Button
                                        size="lg"
                                        variant="secondary"
                                        onClick={() => setShowPrintFlyer(true)}
                                        className={`transition-all hover:scale-105 rounded-xl px-5 py-6 font-bold flex items-center gap-2 ${
                                            isEsports ? 'border-purple-500/20 text-slate-300 hover:text-white bg-purple-950/20 hover:bg-purple-950/40' : ''
                                        }`}
                                    >
                                        📄 Print QR-Poster
                                    </Button>
                                    <Button
                                        size="lg"
                                        onClick={() => setIsPlaying(true)}
                                        disabled={slides.length === 0}
                                        variant="primary"
                                        className={`shadow-xl transition-transform hover:scale-105 px-8 py-6 text-lg rounded-xl flex items-center gap-3 font-bold shrink-0 ${
                                            slides.length === 0 
                                                ? 'opacity-50 cursor-not-allowed' 
                                                : isEsports 
                                                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border border-purple-500/30 hover:shadow-[0_0_25px_rgba(168,85,247,0.45)]' 
                                                    : 'bg-gradient-to-r from-blue-600 to-indigo-600'
                                        }`}
                                    >
                                        <Play size={24} fill="currentColor" /> {t('presentation_page.btn_start')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 border-b border-gray-200/20 pb-1 mb-6">
                        <button
                            onClick={() => setActiveTab('tv')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeTab === 'tv' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Monitor size={18} />
                            {t('presentation_page.tab_tv')}
                        </button>
                        <button
                            onClick={() => setActiveTab('speech')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeTab === 'speech' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Mic size={18} />
                            {t('presentation_page.tab_speech')}
                        </button>
                        <button
                            onClick={() => setActiveTab('predictor')}
                            className={`flex items-center gap-2 px-6 py-3 rounded-t-xl font-bold transition-all relative top-[1px] ${
                                activeTab === 'predictor' 
                                    ? isEsports 
                                        ? 'bg-purple-950/20 text-purple-400 border border-purple-500/20 border-b-transparent shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                                        : 'bg-white text-indigo-600 border border-gray-200 border-b-white' 
                                    : isEsports 
                                        ? 'text-slate-400 hover:text-slate-200 hover:bg-white/5' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <Sparkles size={18} />
                            {t('presentation_page.tab_predictor')}
                        </button>
                    </div>
                </div>

                <HelpTip title={t('presentation_page.help_title')}>
                    <div dangerouslySetInnerHTML={{ __html: t('presentation_page.help_text') }} />
                </HelpTip>

                {/* Content based on Tab */}
                {activeTab === 'predictor' ? (
                    <WinnerPredictor pouleStandings={pouleStandings} />
                ) : activeTab === 'speech' ? (
                    <OpeningSpeechGenerator tournamentName={tournamentName} />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-slideUp" style={{ animationDelay: '0.1s' }}>
                        {/* Sidebar: Add Slides */}
                        <div className="space-y-4">
                            <h3 className={`text-xs font-bold uppercase tracking-wider px-2 ${isEsports ? 'text-purple-300' : 'text-gray-700'}`}>
                                {t('presentation_page.add_slides_title')}
                            </h3>
                            <div className={`glass-strong rounded-3xl p-3 space-y-3 border shadow-lg ${
                                isEsports ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.05)]' : 'bg-white border-gray-100'
                            }`}>
                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 text-white' 
                                            : 'border-blue-100 bg-blue-50/50 hover:bg-blue-50'
                                    }`}
                                    onClick={() => addSlide('standings')}
                                >
                                    <div className="p-3 bg-blue-500 text-white rounded-xl shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform"><List size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('presentation_page.add_slide_standings')}</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.add_slide_standings_desc')}</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-blue-400 group-hover:text-blue-500 transition-colors" />
                                </div>

                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-green-500/20 bg-green-500/5 hover:bg-green-500/10 text-white' 
                                            : 'border-green-100 bg-green-50/50 hover:bg-green-50'
                                    }`}
                                    onClick={() => addSlide('matches')}
                                >
                                    <div className="p-3 bg-green-500 text-white rounded-xl shadow-lg shadow-green-500/20 group-hover:scale-105 transition-transform"><Monitor size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('presentation_page.add_slide_matches')}</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.add_slide_matches_desc')}</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-green-400 group-hover:text-green-500 transition-colors" />
                                </div>

                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-purple-500/20 bg-purple-500/5 hover:bg-purple-500/10 text-white' 
                                            : 'border-purple-100 bg-purple-50/50 hover:bg-purple-50'
                                    }`}
                                    onClick={() => addSlide('program')}
                                >
                                    <div className="p-3 bg-purple-500 text-white rounded-xl shadow-lg shadow-purple-500/20 group-hover:scale-105 transition-transform"><Calendar size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('presentation_page.add_slide_program')}</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.add_slide_program_desc')}</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-purple-400 group-hover:text-purple-500 transition-colors" />
                                </div>

                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-pink-500/20 bg-pink-500/5 hover:bg-pink-500/10 text-white' 
                                            : 'border-pink-100 bg-pink-50/50 hover:bg-pink-50'
                                    }`}
                                    onClick={() => addSlide('image')}
                                >
                                    <div className="p-3 bg-pink-500 text-white rounded-xl shadow-lg shadow-pink-500/20 group-hover:scale-105 transition-transform"><ImageIcon size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('presentation_page.add_slide_image')}</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.add_slide_image_desc')}</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-pink-400 group-hover:text-pink-500 transition-colors" />
                                </div>

                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 text-white' 
                                            : 'border-amber-100 bg-amber-50/50 hover:bg-amber-50'
                                    }`}
                                    onClick={() => addSlide('sponsors')}
                                >
                                    <div className="p-3 bg-amber-500 text-white rounded-xl shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform"><Star size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>Premium Sponsor Wall</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>Geanimeerde showcase van alle actieve sponsoren</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-amber-400 group-hover:text-amber-500 transition-colors" />
                                </div>

                                <div 
                                    className={`p-4 rounded-2xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-95 group flex items-center gap-4 ${
                                        isEsports 
                                            ? 'border-orange-500/20 bg-orange-500/5 hover:bg-orange-500/10 text-white' 
                                            : 'border-orange-100 bg-orange-50/50 hover:bg-orange-50'
                                    }`}
                                    onClick={() => addSlide('custom')}
                                >
                                    <div className="p-3 bg-orange-500 text-white rounded-xl shadow-lg shadow-orange-500/20 group-hover:scale-105 transition-transform"><Type size={20} /></div>
                                    <div>
                                        <h3 className={`font-bold text-base ${isEsports ? 'text-white' : 'text-gray-900'}`}>{t('presentation_page.add_slide_custom')}</h3>
                                        <p className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.add_slide_custom_desc')}</p>
                                    </div>
                                    <Plus size={20} className="ml-auto text-orange-400 group-hover:text-orange-500 transition-colors" />
                                </div>
                            </div>

                            {/* TV Mode Instellingen Card */}
                            <h3 className={`text-xs font-bold uppercase tracking-wider px-2 mt-6 mb-2 ${isEsports ? 'text-purple-300' : 'text-gray-700'}`}>
                                ⚙️ TV Mode Instellingen
                            </h3>
                            <div className={`glass-strong rounded-3xl p-5 border shadow-lg ${
                                isEsports ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.05)]' : 'bg-white border-gray-100'
                            }`}>
                                <div className="flex items-start gap-4">
                                    <div className={`p-3 rounded-xl shrink-0 ${isEsports ? 'bg-purple-500/10 text-purple-400' : 'bg-indigo-50 text-indigo-600'}`}>
                                        <Mic size={22} className={voiceAnnouncer ? 'animate-pulse' : ''} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`font-bold text-sm block ${isEsports ? 'text-white font-black' : 'text-gray-900'}`}>Live TV-Omroeper</span>
                                            <label className="relative inline-flex items-center cursor-pointer select-none">
                                                <input 
                                                    type="checkbox" 
                                                    checked={voiceAnnouncer}
                                                    onChange={(e) => {
                                                        const active = e.target.checked
                                                        setVoiceAnnouncer(active)
                                                        if (active && window.speechSynthesis) {
                                                            window.speechSynthesis.cancel()
                                                            const testUtterance = new SpeechSynthesisUtterance("Live omroeper geactiveerd!")
                                                            testUtterance.lang = "nl-NL"
                                                            window.speechSynthesis.speak(testUtterance)
                                                        }
                                                    }}
                                                    className="sr-only peer" 
                                                />
                                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                                            </label>
                                        </div>
                                        <p className={`text-[10px] leading-relaxed ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Laat de kantine-speakers uitslagen en rondes automatisch omroepen met een natuurlijke Nederlandse stem.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main: Slide List */}
                        <div className="lg:col-span-2">
                            <div className={`glass-strong rounded-3xl p-6 min-h-[500px] border shadow-xl ${
                                isEsports ? 'bg-purple-950/10 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.05)] text-white' : 'bg-white border-gray-100'
                            }`}>
                                {loading ? (
                                    <div className="flex justify-center p-12 animate-pulse">
                                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-purple-600"></div>
                                    </div>
                                ) : slides.length === 0 ? (
                                    <div className="text-center py-24 text-gray-400 flex flex-col items-center">
                                        <div className={`p-6 rounded-full mb-6 border ${isEsports ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50 border-gray-100'}`}>
                                            <Monitor size={48} className={`opacity-40 ${isEsports ? 'text-purple-400' : 'text-gray-900'}`} />
                                        </div>
                                        <h3 className={`text-xl font-bold mb-2 ${isEsports ? 'text-white' : 'text-gray-800'}`}>{t('presentation_page.empty_title')}</h3>
                                        <p className={`max-w-xs mx-auto text-sm ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{t('presentation_page.empty_desc')}</p>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="flex justify-between items-center mb-6 border-b pb-4 border-gray-200/20">
                                            <h3 className={`text-lg font-bold flex items-center gap-2.5 ${isEsports ? 'text-white' : 'text-gray-700'}`}>
                                                <Badge variant="primary" size="lg" className={isEsports ? 'bg-purple-500 text-white' : ''}>{slides.length}</Badge> {t('presentation_page.slides_list_title')}
                                            </h3>
                                            <span className={`text-xs uppercase tracking-wider font-bold ${isEsports ? 'text-slate-400' : 'text-gray-400'}`}>{t('presentation_page.slides_drag_hint')}</span>
                                        </div>

                                        <div className="space-y-4">
                                            {slides.map((slide, index) => (
                                                <div key={slide.id} className="relative">
                                                    <div className={`absolute -left-3 top-7 text-xs font-bold w-6 text-right ${isEsports ? 'text-purple-400' : 'text-gray-300'}`}>
                                                        {index + 1}
                                                    </div>
                                                    <SlidePreview
                                                        slide={slide}
                                                        onDelete={deleteSlide}
                                                        onMoveUp={() => moveSlide(index, 'up')}
                                                        onMoveDown={() => moveSlide(index, 'down')}
                                                        isFirst={index === 0}
                                                        isLast={index === slides.length - 1}
                                                        isEsports={isEsports}
                                                    />
                                                    {/* Edit inputs */}
                                                    {(slide.type === 'custom' || slide.type === 'image' || slide.type === 'sponsors') && (
                                                        <div className={`ml-6 mb-6 p-6 rounded-2xl border transition-all ${
                                                            isEsports 
                                                                ? 'bg-purple-950/5 border-purple-500/20 shadow-inner' 
                                                                : 'bg-gray-50/50 border-gray-200/60 shadow-sm'
                                                        }`}>
                                                            <div className="grid grid-cols-1 gap-5">
                                                                {slide.type === 'sponsors' && (
                                                                    <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl text-center">
                                                                        <Star className="mx-auto mb-2 text-amber-500" size={24} />
                                                                        <h4 className="font-bold text-amber-600 dark:text-amber-400 mb-1">Geautomatiseerde Sponsor Wall</h4>
                                                                        <p className={`text-sm ${isEsports ? 'text-slate-300' : 'text-gray-600'}`}>
                                                                            Deze slide haalt <strong>automatisch</strong> al jouw actieve sponsoren op (inclusief logo) uit de Sponsor Manager.<br/><br/>
                                                                            Wil je sponsoren toevoegen of verwijderen? Ga in het linkermenu naar <strong>Sponsor Assistent</strong>.
                                                                        </p>
                                                                    </div>
                                                                )}
                                                                {slide.type !== 'sponsors' && (
                                                                    <div>
                                                                        <label className={`block text-xs font-bold mb-1.5 uppercase tracking-wide ${isEsports ? 'text-purple-300' : 'text-gray-500'}`}>{t('presentation_page.label_title')}</label>
                                                                        <Input
                                                                            value={slide.title}
                                                                            onChange={(e) => {
                                                                                const newSlides = [...slides];
                                                                                newSlides[index].title = e.target.value;
                                                                                setSlides(newSlides);
                                                                            }}
                                                                            onBlur={async () => await supabase.from('slides').update({ title: slide.title }).eq('id', slide.id)}
                                                                            className={isEsports ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' : ''}
                                                                        />
                                                                    </div>
                                                                )}
                                                                {slide.type === 'custom' && (
                                                                    <div>
                                                                        <label className={`block text-xs font-bold mb-1.5 uppercase tracking-wide ${isEsports ? 'text-purple-300' : 'text-gray-500'}`}>{t('presentation_page.label_content')}</label>
                                                                        <textarea
                                                                            className={`w-full rounded-xl p-3 text-sm focus:ring-2 focus:ring-[var(--primary)] outline-none transition-all ${
                                                                                isEsports 
                                                                                    ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500' 
                                                                                    : 'border-gray-200 bg-white/80'
                                                                            }`}
                                                                            rows={3}
                                                                            value={slide.content || ''}
                                                                            onChange={(e) => {
                                                                                const newSlides = [...slides];
                                                                                newSlides[index].content = e.target.value;
                                                                                setSlides(newSlides);
                                                                            }}
                                                                            onBlur={async () => await supabase.from('slides').update({ content: slide.content }).eq('id', slide.id)}
                                                                        />
                                                                    </div>
                                                                )}
                                                                {slide.type === 'image' && (
                                                                    <div>
                                                                        <MultiImageUploader
                                                                            label={t('presentation_page.label_images')}
                                                                            value={slide.image_url || slide.content || ''}
                                                                            onChange={async (newUrl) => {
                                                                                const newSlides = [...slides];
                                                                                newSlides[index].image_url = newUrl;
                                                                                newSlides[index].content = newUrl;
                                                                                setSlides(newSlides);
                                                                                await supabase.from('slides').update({ image_url: newUrl, content: newUrl }).eq('id', slide.id);
                                                                            }}
                                                                            placeholder={t('presentation_page.upload_placeholder')}
                                                                            previewHeight="h-48"
                                                                            allowMultiple={true}
                                                                        />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <label className={`block text-xs font-bold mb-1.5 uppercase tracking-wide ${isEsports ? 'text-purple-300' : 'text-gray-500'}`}>{t('presentation_page.label_duration')}</label>
                                                                    <input
                                                                        type="number"
                                                                        min="5"
                                                                        max="300"
                                                                        className={`w-28 rounded-xl p-2.5 text-sm text-center font-bold border transition-all ${
                                                                            isEsports 
                                                                                ? 'bg-purple-950/20 border-purple-500/25 text-white focus:border-purple-500 focus:outline-none' 
                                                                                : 'border-gray-200'
                                                                        }`}
                                                                        value={slide.duration || 15}
                                                                        onChange={(e) => {
                                                                            const newSlides = [...slides];
                                                                            newSlides[index].duration = parseInt(e.target.value);
                                                                            setSlides(newSlides);
                                                                        }}
                                                                        onBlur={async () => await supabase.from('slides').update({ duration: slide.duration }).eq('id', slide.id)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showPrintFlyer && createPortal(
                <div className="fixed inset-0 z-[100000] bg-white text-black flex items-center justify-center font-sans print-only">
                    <style>{`
                        @media print {
                            body {
                                background: white !important;
                                color: black !important;
                            }
                            .no-print {
                                display: none !important;
                            }
                            .print-only {
                                display: flex !important;
                                width: 100vw !important;
                                height: 100vh !important;
                                position: fixed !important;
                                top: 0 !important;
                                left: 0 !important;
                                z-index: 99999 !important;
                                background: white !important;
                                color: black !important;
                                padding: 2cm !important;
                                box-sizing: border-box !important;
                                align-items: center !important;
                                justify-content: center !important;
                                flex-direction: column !important;
                            }
                        }
                    `}</style>
                    <div className="w-full max-w-2xl border-[12px] border-black p-12 flex flex-col items-center justify-between text-center min-h-[85vh] bg-white rounded-3xl relative">
                        {/* Decorative Corners */}
                        <div className="absolute top-4 left-4 w-8 h-8 border-t-4 border-l-4 border-black"></div>
                        <div className="absolute top-4 right-4 w-8 h-8 border-t-4 border-r-4 border-black"></div>
                        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-4 border-l-4 border-black"></div>
                        <div className="absolute bottom-4 right-4 w-8 h-8 border-b-4 border-r-4 border-black"></div>

                        <div>
                            <div className="inline-block bg-black text-white px-8 py-3 rounded-full text-xs font-black tracking-widest uppercase mb-6">
                                Live Speelschema & Standen
                            </div>
                            <h1 className="text-5xl font-black text-black uppercase tracking-tight mb-2 leading-none">
                                {tournamentName || "Toernooi"}
                            </h1>
                            <p className="text-gray-500 font-bold uppercase tracking-wider text-sm mt-3">
                                Real-time uitslagen en live poule standen
                            </p>
                        </div>

                        <div className="my-8 p-4 bg-white border-[4px] border-black rounded-3xl shadow-xl flex items-center justify-center">
                            <QRCodeSVG 
                                value={window.location.origin + '/live/' + tournamentId} 
                                size={280} 
                                level="H" 
                                includeMargin={true}
                                fgColor="#000000"
                                bgColor="#FFFFFF"
                            />
                        </div>

                        <div className="max-w-md">
                            <h3 className="text-2xl font-black uppercase text-black tracking-tight mb-2">
                                📱 SCAN EN VOLG LIVE!
                            </h3>
                            <p className="text-gray-600 font-medium text-base leading-relaxed">
                                Open de camera op je smartphone en richt deze op de QR-code om direct alle wedstrijden, standen en uitslagen live te volgen.
                            </p>
                        </div>

                        <div className="w-full border-t border-gray-300 pt-6 mt-6 flex justify-between items-center text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <span>Aangeboden door de Toernooiorganisatie</span>
                            <span className="text-black font-black">ProPoule.nl</span>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

const SlidePreview = ({ slide, onDelete, onMoveUp, onMoveDown, isFirst, isLast, isEsports }) => {
    const { t } = useTranslation()

    const iconMap = {
        standings: <List size={20} className={isEsports ? 'text-purple-400' : 'text-blue-500'} />,
        matches: <Monitor size={20} className={isEsports ? 'text-cyan-400' : 'text-green-500'} />,
        program: <Calendar size={20} className={isEsports ? 'text-indigo-400' : 'text-purple-500'} />,
        image: <ImageIcon size={20} className={isEsports ? 'text-pink-400' : 'text-pink-500'} />,
        custom: <Type size={20} className={isEsports ? 'text-orange-400' : 'text-orange-500'} />
    }

    const titleMap = {
        standings: t('presentation_page.slide_type_standings'),
        matches: t('presentation_page.slide_type_matches'),
        program: t('presentation_page.slide_type_program'),
        image: t('presentation_page.slide_type_image'),
        custom: t('presentation_page.slide_type_custom')
    }

    return (
        <div className={`glass-subtle border rounded-2xl p-5 flex items-center justify-between shadow-sm mb-3 group hover:scale-[1.01] transition-all duration-300 ${
            isEsports 
                ? 'bg-purple-950/10 border-purple-500/15 hover:border-purple-500/30 text-white shadow-[0_0_15px_rgba(168,85,247,0.03)]' 
                : 'bg-white border-gray-100 hover:shadow-md'
        }`}>
            <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                    <button
                        onClick={onMoveUp}
                        disabled={isFirst}
                        className={`p-1.5 rounded-lg transition-colors ${
                            isFirst 
                                ? 'text-gray-300 opacity-20' 
                                : isEsports 
                                    ? 'text-slate-400 hover:text-white hover:bg-white/5' 
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title={t('presentation_page.btn_move_up')}
                    >
                        <ArrowUp size={16} />
                    </button>
                    <button
                        onClick={onMoveDown}
                        disabled={isLast}
                        className={`p-1.5 rounded-lg transition-colors ${
                            isLast 
                                ? 'text-gray-300 opacity-20' 
                                : isEsports 
                                    ? 'text-slate-400 hover:text-white hover:bg-white/5' 
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                        title={t('presentation_page.btn_move_down')}
                    >
                        <ArrowDown size={16} />
                    </button>
                </div>
                <div className={`p-3 rounded-xl border transition-all ${
                    isEsports 
                        ? 'bg-purple-950/40 border-purple-500/20' 
                        : 'bg-white border-gray-100 shadow-sm'
                }`}>{iconMap[slide.type] || <Monitor />}</div>
                <div>
                    <h3 className={`font-bold text-lg ${isEsports ? 'text-white font-black' : 'text-gray-800'}`}>{slide.title || titleMap[slide.type]}</h3>
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant="neutral" size="sm" className={isEsports ? 'bg-purple-500/20 text-purple-300 border border-purple-500/20' : ''}>
                            {slide.duration} {t('presentation_page.unit_sec')}
                        </Badge>
                        <span className={`text-xs ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>{titleMap[slide.type]}</span>
                    </div>
                </div>
            </div>
            <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onDelete(slide.id)} 
                className={`rounded-xl p-2.5 transition-colors ${
                    isEsports 
                        ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' 
                        : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                }`}
            >
                <Trash2 size={18} />
            </Button>
        </div>
    )
}
